import { useState } from 'react';
import { useMutation } from 'react-query';

import { Address } from '@/domain/address/models/address';
import { ModalAddress } from '@/presentation/Address';
import { useAddressContext } from '@/shared/contexts/AddressContext';
import { useAuthContext } from '@/shared/contexts/AuthContext';
import { executeEvent } from '@/shared/events/Firebase/firebaseEvents';
import { useToast } from '@/shared/hooks/useToast';
import { unmask } from '@/shared/utils/regex';

export interface ModalAddressFactoryProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

export const ModalAddressFactory: React.FC<ModalAddressFactoryProps> = ({
  isOpen,
  onClose,
  onOpen,
}) => {
  const { toast, defaultErrorToast } = useToast();

  const [isOpenDialogDeleteAddress, setIsOpenDialogDeleteAddress] =
    useState(false);

  const { isLoggedin } = useAuthContext();

  const {
    currentAddress,
    currentAddressId,
    geoLocation,
    selectAddressToEdit,
    userAddresses,
    clearState,
    setCurrentAddressId,
    setCurrentStep,
    setAddressToEditClickOnCard,
    onCreateAddress,
    onDeleteAddress,
    onSelectUserAddress,
    onUpdateAddress,
  } = useAddressContext();

  const addressConfirmationMutation = useMutation(
    (data: Address) => {
      const newAddress: Address = {
        title: data.title,
        street: data.street,
        state: selectAddressToEdit?.state ?? '',
        referencePoint: data.referencePoint,
        postalCode: unmask(data.postalCode),
        number: data.number,
        neighborhood: data.neighborhood,
        complement: data.complement,
        city: selectAddressToEdit?.city ?? '',
        long: String(geoLocation.longitude),
        lat: String(geoLocation.latitude),
      };

      if (!isLoggedin) return Promise.resolve([newAddress]);

      if (!currentAddressId) return onCreateAddress(newAddress);

      return onUpdateAddress(currentAddressId, newAddress);
    },
    {
      onSuccess: data => {
        const isNewAddress = data.length !== userAddresses.length;
        const firstAddress = data[0];

        clearState();
        setCurrentStep('create');

        if (isNewAddress) {
          onClose();
        }

        if (isNewAddress || currentAddress?.id === firstAddress.id) {
          onSelectUserAddress(firstAddress, isNewAddress);
        }
      },
    },
  );

  const deleteAddressMutation = useMutation(
    () => onDeleteAddress(currentAddressId),
    {
      onSuccess: () => {
        setIsOpenDialogDeleteAddress(false);
        setCurrentAddressId('');
        deleteAddressMutation.reset();
      },
      onError: defaultErrorToast,
    },
  );

  const handleOnSelectUserAddress = (addressToSelect: Address) => {
    try {
      executeEvent('click_select_address', {});
      onSelectUserAddress(addressToSelect);
      clearState();
      onClose();
    } catch {
      toast({
        type: 'error',
        title: 'Serviço indisponível!',
        subTitle: `Houve um erro ao selecionar o endereço.`,
      });
    }
  };

  const handleOnEditAddress = (id: string) => {
    executeEvent('click_edit_address', {});
    setAddressToEditClickOnCard(id);
    setCurrentStep('edit');
  };

  const onCloseDialogDeleteConfirmation = () => {
    setIsOpenDialogDeleteAddress(false);
    setCurrentAddressId('');
  };

  const onOpenDialogDeleteConfirmation = (id: string) => {
    setIsOpenDialogDeleteAddress(true);
    setCurrentAddressId(id);
  };

  return (
    <ModalAddress
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      handleOnSelectUserAddress={handleOnSelectUserAddress}
      handleOnEditAddress={handleOnEditAddress}
      isOpenDialogDeleteAddress={isOpenDialogDeleteAddress}
      onOpenDialogDeleteConfirmation={onOpenDialogDeleteConfirmation}
      onCloseDialogDeleteConfirmation={onCloseDialogDeleteConfirmation}
      addressConfirmationMutation={addressConfirmationMutation}
      deleteAddressMutation={deleteAddressMutation}
    />
  );
};
